'use client';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useMemo } from 'react';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import {
  ModelVersionRemainingTrialsBadge,
  ModelVersionSelectItemContent,
} from '@/app/components/shared/ModelVersionSelectItemContent';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValueWithImageOnly,
} from '@/app/components/ui/select';
import { toast } from '@/app/components/ui/use-toast';
import { MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP } from '@/app/constants';
import { useModelItems } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { FormTabType, ModelVersion } from '@/app/types';
import {
  getModelCapabilityKey,
  getModelVersionRemainingTrials,
  getPromptMaxLength,
  reportEvent,
  syncImageReferencePrompts,
  validateAndCorrectSettings,
} from '@/app/utils';
import { useLocale, usePathname } from '@/i18n/routing';

// 工具函数：合并两个对象的共同属性
// biome-ignore lint/suspicious/noExplicitAny: <any>
const mergeCommonProperties = <T extends Record<string, any>>(target: T, source: Partial<T>): T => {
  const commonKeys = Object.keys(target).filter((key) => key in source);
  const commonProperties = Object.fromEntries(commonKeys.map((key) => [key, source[key]]));

  return {
    ...target,
    ...commonProperties,
  };
};
export const ModelSelection: FC<{ type: Exclude<FormTabType, 'scene'> }> = ({ type }) => {
  const t = useTranslations();
  const locale = useLocale();
  const pathname = usePathname();
  const modelItems = useModelItems(type);

  const {
    modelVersion,
    updateFormParams,
    character2videoFormParams,
    updateModelVersion,
    formParams,
    remainingTrialsQ2Pro,
    remainingTrialsQ3T2VI2V,
    remainingTrialsQ3C2V,
    remainingTrialsQ2T2V,
    remainingTrialsQ2C2V,
    remainingTrialsQ2T2I,
    remainingTrialsQ2R2I,
    remainingTrialsImage25T2IFast,
    remainingTrialsImage25T2IPro,
    remainingTrialsImage25R2IFast,
    remainingTrialsImage25R2IPro,
    img2videoFormParams,
    setHiddenReferencePrompts,
    hiddenReferencePrompts,
    trialsQ3T2VI2VDurations,
    trialsQ3C2VDurations,
  } = useStore((state) => ({
    modelVersion: state[`${type}FormParams`].modelVersion,
    updateFormParams: state.updateFormParams,
    updateModelVersion: state.updateModelVersion,
    character2videoFormParams: state.character2videoFormParams,
    formParams: state[`${type}FormParams`],
    remainingTrialsQ2Pro: state.trialsQ2Pro?.remaining,
    remainingTrialsQ3T2VI2V: state.trialsQ3T2VI2V?.remaining,
    remainingTrialsQ3C2V: state.trialsQ3C2V?.remaining,
    remainingTrialsQ2T2V: state.trialsQ2T2V?.remaining,
    remainingTrialsQ2C2V: state.trialsQ2C2V?.remaining,
    remainingTrialsQ2T2I: state.trialsQ2T2I?.remaining,
    remainingTrialsQ2R2I: state.trialsQ2R2I?.remaining,
    remainingTrialsImage25T2IFast: state.trialsImage25T2IFast?.remaining,
    remainingTrialsImage25T2IPro: state.trialsImage25T2IPro?.remaining,
    remainingTrialsImage25R2IFast: state.trialsImage25R2IFast?.remaining,
    remainingTrialsImage25R2IPro: state.trialsImage25R2IPro?.remaining,
    img2videoFormParams: state.img2videoFormParams,
    setHiddenReferencePrompts: state.setHiddenReferencePrompts,
    hiddenReferencePrompts: state.hiddenReferencePrompts,
    trialsQ3T2VI2VDurations: state.trialsQ3T2VI2V?.durations,
    trialsQ3C2VDurations: state.trialsQ3C2V?.durations,
  }));

  // 获取 img2video 的有效图片数量
  const validImageCount = useMemo(() => {
    if (type !== 'img2video') {
      return 0;
    }
    return img2videoFormParams.imagePrompts?.filter((p) => p !== null).length || 0;
  }, [type, img2videoFormParams.imagePrompts]);

  // 图生视频里，只有 Q2 支持超过 2 张图；hover 其他模型时统一提示最多 2 张
  const shouldShowTooltip = (itemValue: ModelVersion) => {
    let maxCount = 0;
    if (itemValue !== MODEL_VERSION.Q2 && type === 'img2video') {
      maxCount = 2;
    }
    return maxCount > 0
      ? {
          show: validImageCount > maxCount,
          maxCount,
        }
      : undefined;
  };

  const handleSelectModelVersion = (value: ModelVersion) => {
    // 把前一个模型中的设置同步到下一个模型中，如果下一个模型中没有，则不覆盖
    const prevSettingsKey = MODEL_VERSION_SETTINGS_MAP[modelVersion];
    const nextSettingsKey = MODEL_VERSION_SETTINGS_MAP[value];
    const prevSettings = formParams[prevSettingsKey];
    const nextSettings = formParams[nextSettingsKey];
    const newSettings = validateAndCorrectSettings({
      version: value,
      newSettings: mergeCommonProperties(nextSettings, prevSettings),
      t,
      type,
    });
    updateFormParams(type, nextSettingsKey, newSettings);

    //同步图片和草稿
    if (pathname.includes('character2video')) {
      const updates = syncImageReferencePrompts({
        prevVersion: modelVersion,
        nextVersion: value,
        formParams: character2videoFormParams,
        locale,
        hiddenReferencePrompts,
      });
      if (updates.referencePrompts) {
        updateFormParams('character2video', 'referencePrompts', updates.referencePrompts);
      }
      if (updates.textPromptWithReference) {
        updateFormParams('character2video', 'textPromptWithReference', updates.textPromptWithReference);
      }
      if (updates.imagePrompts) {
        updateFormParams('character2video', 'imagePrompts', updates.imagePrompts);
      }
      if (updates.textPrompt !== undefined) {
        updateFormParams('character2video', 'textPrompt', updates.textPrompt);
      }
      // 存储隐藏的项（如果为空数组，会替换掉之前存储的隐藏项，用于清除已删除的项）
      if (updates.hiddenItems !== undefined) {
        setHiddenReferencePrompts(getModelCapabilityKey(modelVersion), updates.hiddenItems);
      }
    }
    if (updateModelVersion(type, value)) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: getPromptMaxLength(value) }),
      });
    }

    reportEvent('model_version_switch_click', { version_result: value });
  };

  const freeTrialBadge = useMemo(() => {
    const remainingTrials = getModelVersionRemainingTrials({
      type,
      modelVersion,
      settings: formParams[MODEL_VERSION_SETTINGS_MAP[modelVersion]],
      trials: {
        remainingTrialsQ2Pro,
        remainingTrialsQ3T2VI2V,
        remainingTrialsQ3C2V,
        remainingTrialsQ2T2V,
        remainingTrialsQ2C2V,
        remainingTrialsQ2T2I,
        remainingTrialsQ2R2I,
        remainingTrialsImage25T2IFast,
        remainingTrialsImage25T2IPro,
        remainingTrialsImage25R2IFast,
        remainingTrialsImage25R2IPro,
        trialsQ3T2VI2VDurations,
        trialsQ3C2VDurations,
      },
    });

    return remainingTrials ? <ModelVersionRemainingTrialsBadge remainingTrials={remainingTrials} /> : null;
  }, [
    type,
    modelVersion,
    remainingTrialsQ2Pro,
    remainingTrialsQ3T2VI2V,
    remainingTrialsQ3C2V,
    remainingTrialsQ2T2V,
    remainingTrialsQ2C2V,
    remainingTrialsQ2T2I,
    remainingTrialsQ2R2I,
    remainingTrialsImage25T2IFast,
    remainingTrialsImage25T2IPro,
    remainingTrialsImage25R2IFast,
    remainingTrialsImage25R2IPro,
    formParams,
    trialsQ3T2VI2VDurations,
    trialsQ3C2VDurations,
  ]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      return;
    }
    // Always start at the top: preferred models are listed first; Radix may otherwise land at the bottom.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = document.querySelector<HTMLElement>('[data-radix-select-viewport]');
        if (viewport) {
          viewport.scrollTop = 0;
        }
      });
    });
  }, []);

  return (
    <Select value={modelVersion} onValueChange={handleSelectModelVersion} onOpenChange={handleOpenChange}>
      <SelectTrigger
        id="model-selection"
        className="h-10 w-full rounded-10 border-none bg-system-white04 p-2 text-system-white"
      >
        <SelectValueWithImageOnly
          selectedItem={modelItems.find((item) => item.value === modelVersion)}
          activityTip={freeTrialBadge}
        />
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)] rounded-8 border border-system-white02 border-solid bg-system-bg02 p-1 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.32)]">
        <SelectGroup className="flex flex-col gap-1">
          {modelItems.map((item) => {
            const showTooltip = shouldShowTooltip(item.value as ModelVersion);
            const tooltipContent = showTooltip?.show
              ? t('create.createForm.settings.modelVersion.switchModelTooltip', {
                  count: showTooltip.maxCount,
                })
              : undefined;

            const itemInnerContent = (
              <ModelVersionSelectItemContent
                image={item.image}
                title={item.title}
                description={item.description}
                isNew={item.isNew}
              />
            );

            return (
              <SelectItem key={item.value} value={item.value} className="p-2">
                {showTooltip?.show && tooltipContent ? (
                  <CustomTooltip
                    content={tooltipContent}
                    trigger="hover"
                    side="right"
                    sideOffset={8}
                    contentClassName="max-w-[280px]"
                    disableHoverableContent={true}
                  >
                    {itemInnerContent}
                  </CustomTooltip>
                ) : (
                  itemInnerContent
                )}
              </SelectItem>
            );
          })}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};
